import { z } from "zod";
import { UserRole, UserStatus, WalletType, ListingStatus, ListingCategory, SellerBadge, OfferStatus, OfferProposerRole, ListingFulfillmentType, LicenseKeyStatus, AccountPlatform, AccountTransferStage, DomainTransferStage, NftTransferStage, NotificationType, UploadStatus, FinancialStatus, DeliveryStatus, DisputeStatus, TicketStatus, TicketPriority, TicketContactMethod, PostStatus, } from "./enums.js";
export const addressSchema = z
    .string()
    .regex(/^0x[a-fA-F0-9]{40}$/, "must be a 0x-prefixed 40-hex-char address");
export const bytes32Schema = z
    .string()
    .regex(/^0x[a-fA-F0-9]{64}$/, "must be a 0x-prefixed 32-byte hex value");
export const bigintBaseUnitsSchema = z
    .string()
    .regex(/^\d+$/, "must be a non-negative integer string of base units");
export const positiveBigintBaseUnitsSchema = bigintBaseUnitsSchema.refine((v) => BigInt(v) > 0n, {
    message: "must be a positive integer string of base units",
});
export const uuidSchema = z.string().uuid();
export const sellerRatingSummarySchema = z.object({
    average: z.number().min(1).max(5).nullable(),
    count: z.number().int().nonnegative(),
});
export const sellerBadgeSchema = z.enum([SellerBadge.TOP_RATED, SellerBadge.TRUSTED_SELLER]);
export const listingFulfillmentTypeSchema = z.enum([
    ListingFulfillmentType.FILE,
    ListingFulfillmentType.LICENSE_KEY,
    ListingFulfillmentType.ACCOUNT_TRANSFER,
    ListingFulfillmentType.DOMAIN_TRANSFER,
    ListingFulfillmentType.NFT_TRANSFER,
]);
export const accountPlatformSchema = z.enum([
    AccountPlatform.YOUTUBE,
    AccountPlatform.X_TWITTER,
    AccountPlatform.TIKTOK,
    AccountPlatform.INSTAGRAM,
    AccountPlatform.DISCORD,
    AccountPlatform.OTHER,
]);
export const accountTransferStageSchema = z.enum([
    AccountTransferStage.AWAITING_SELLER_HANDOFF,
    AccountTransferStage.PLATFORM_VERIFYING,
    AccountTransferStage.READY_FOR_BUYER,
    AccountTransferStage.CREDENTIALS_REVEALED,
]);
export const domainTransferStageSchema = z.enum([
    DomainTransferStage.AWAITING_SELLER_CODE,
    DomainTransferStage.READY_FOR_BUYER,
    DomainTransferStage.CODE_REVEALED,
]);
export const nftTransferStageSchema = z.enum([
    NftTransferStage.AWAITING_TRANSFER,
    NftTransferStage.DELIVERED,
]);
export const authPrincipalSchema = z.object({
    userId: uuidSchema,
    cdpUserId: z.string(),
    walletId: uuidSchema,
    walletAddress: addressSchema,
    walletType: z.enum([WalletType.EOA, WalletType.SMART_ACCOUNT]),
    role: z.enum([UserRole.USER, UserRole.MODERATOR, UserRole.ADMIN]),
    /** Public display name, shown to counterparties on listings/orders. Null until the user sets
     *  one — never auto-derived from their email, which is private. */
    username: z.string().nullable(),
    /** True only on the request that just created this account — see services/identity.ts. Used
     *  to fire a one-time signup conversion event; not meaningful to compare across requests. */
    isNewUser: z.boolean(),
    /** One-way SHA-256 of the account's email (never the raw address) — see services/identity.ts.
     *  Used for X Ads' Enhanced/Advanced Matching on the signup conversion event. Null in the rare
     *  case the account has no email on file. */
    emailHash: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
/**
 * Names reserved to stop a seller from impersonating the platform or its staff — a real
 * marketplace-trust concern, since the username is the primary thing a buyer sees identifying
 * who they're transacting with. Matched case-insensitively against the normalized (lowercased)
 * username, so `Admin`/`ADMIN` are caught too.
 */
const RESERVED_USERNAMES = new Set([
    "admin",
    "administrator",
    "support",
    "help",
    "helpdesk",
    "moderator",
    "mod",
    "staff",
    "team",
    "official",
    "system",
    "root",
    "security",
    "billing",
    "payments",
    "escrow",
    "wicked",
    "wickedescrow",
    "arbitrator",
    "treasury",
    "api",
    "www",
    "null",
    "undefined",
    "anonymous",
    "me",
]);
/**
 * Usernames are stored and compared lowercased so `Alice` and `alice` can't coexist and be
 * confused for one another. Underscores and hyphens are allowed inside the name but not at
 * either edge, and can't repeat — `a__b` and `a--b` read as visually distinct accounts at a
 * glance while being trivially confusable, which is exactly the impersonation vector the
 * reserved-name list above also guards against.
 */
export const usernameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, "must be at least 3 characters")
    .max(30, "must be at most 30 characters")
    .regex(/^[a-z0-9](?:[a-z0-9]|[_-](?![_-]))*[a-z0-9]$/, "may use letters, numbers, single underscores and hyphens, and must start and end with a letter or number")
    .refine((value) => !RESERVED_USERNAMES.has(value), { message: "is reserved" });
export const updateProfileRequestSchema = z.object({
    username: usernameSchema,
});
/** Contact/location detail — never shown to counterparties, same privacy stance as email; only
 *  ever returned to the authenticated owner on their own profile. All optional and independent
 *  of the public `username`/`AuthPrincipal`. */
export const profileDetailsResponseSchema = z.object({
    phone: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
    locationLabel: z.string().nullable(),
    /** Always a usable image URL — Gravatar's `d=identicon` fallback means this resolves even
     *  when the account's email has no registered Gravatar. */
    avatarUrl: z.string().url(),
});
export const updateProfileDetailsRequestSchema = z.object({
    phone: z.string().trim().max(40).optional(),
    addressLine1: z.string().trim().max(200).optional(),
    addressLine2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    region: z.string().trim().max(100).optional(),
    postalCode: z.string().trim().max(20).optional(),
    country: z.string().trim().max(100).optional(),
    locationLabel: z.string().trim().max(140).optional(),
});
// ---------------------------------------------------------------------------
// Seller analytics
// ---------------------------------------------------------------------------
export const sellerAnalyticsMonthSchema = z.object({
    /** "YYYY-MM", oldest first. */
    month: z.string(),
    revenueBaseUnits: bigintBaseUnitsSchema,
    orderCount: z.number().int().nonnegative(),
});
export const sellerAnalyticsSchema = z.object({
    totalOrders: z.number().int().nonnegative(),
    /** RELEASED orders — escrow has actually paid out. */
    completedSales: z.number().int().nonnegative(),
    /** FUNDED, not yet RELEASED or DISPUTED — money is escrowed but not yet the seller's. */
    pendingSales: z.number().int().nonnegative(),
    disputedOrders: z.number().int().nonnegative(),
    refundedOrders: z.number().int().nonnegative(),
    /** Sum of `principalBaseUnits` for RELEASED orders — what actually reached the seller.
     *  Excludes the platform fee, which never belongs to the seller in the first place. */
    totalRevenueBaseUnits: bigintBaseUnitsSchema,
    /** Sum of `principalBaseUnits` for FUNDED orders — escrowed, not yet released. */
    pendingRevenueBaseUnits: bigintBaseUnitsSchema,
    averageOrderValueBaseUnits: bigintBaseUnitsSchema.nullable(),
    sellerRating: sellerRatingSummarySchema,
    /** Server-computed, never self-declared — see apps/api/src/services/badges.ts. */
    sellerBadges: z.array(sellerBadgeSchema),
    /** Last 12 months of RELEASED-order revenue, oldest first, including zero-order months. */
    revenueByMonth: z.array(sellerAnalyticsMonthSchema),
});
// ---------------------------------------------------------------------------
// Listings
// ---------------------------------------------------------------------------
export const listingCategorySchema = z.enum([
    ListingCategory.GAME_ACCOUNTS,
    ListingCategory.SOCIAL_MEDIA_ACCOUNTS,
    ListingCategory.GAME_CURRENCY_ITEMS,
    ListingCategory.SOFTWARE_LICENSES,
    ListingCategory.GIFT_CARDS,
    ListingCategory.CLOUD_CREDITS,
    ListingCategory.DOMAINS,
    ListingCategory.SUBSCRIPTIONS,
    ListingCategory.DIGITAL_ART_COLLECTIBLES,
    ListingCategory.EBOOKS_AND_MEDIA,
    ListingCategory.DESIGN_ASSETS,
    ListingCategory.OTHER,
]);
/** Lowercased at parse time (WHOIS/DNS are case-insensitive, and this must match exactly what
 *  services/domainVerification.ts looks up). Doesn't handle IDN/punycode domains in v1 — a
 *  documented limitation, not an oversight (see MARKETPLACE_EXPANSION_PLAN.md). */
export const domainNameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "must be a valid domain name (e.g. example.com)");
/** Decimal-string token id (never parsed to a number — uint256 can exceed what JS/Postgres
 *  integers hold safely). Matches nftContractAddress's round-trip-as-text treatment. */
export const nftTokenIdSchema = z
    .string()
    .trim()
    .regex(/^\d+$/, "must be a non-negative integer token id")
    .max(78, "not a valid uint256 token id");
const createListingRequestBaseSchema = z.object({
    title: z.string().trim().min(3).max(140),
    description: z.string().trim().min(1).max(10_000),
    priceBaseUnits: positiveBigintBaseUnitsSchema,
    // Optional, defaulting to OTHER (mirrors the DB column default) so an older/not-yet-updated
    // client that doesn't send a category can still create a listing — it just lands
    // uncategorized until the seller edits it.
    category: listingCategorySchema.default(ListingCategory.OTHER),
    /** Immutable after creation — see services/listings.ts. Defaults to FILE, the original flow. */
    fulfillmentType: listingFulfillmentTypeSchema.default(ListingFulfillmentType.FILE),
    /** Required iff fulfillmentType is ACCOUNT_TRANSFER, forbidden otherwise — enforced below and
     *  backstopped by a DB CHECK constraint (see the add_account_transfers migration). */
    accountPlatform: accountPlatformSchema.optional(),
    /** Optional even for ACCOUNT_TRANSFER — only YOUTUBE/X_TWITTER have a public stats API to
     *  verify against (services/platformStats.ts), so this is a trust signal, never a requirement.
     *  A bare username/handle, without a leading "@" or full URL — the server normalizes as needed. */
    accountHandle: z.string().trim().min(1).max(100).optional(),
    /** Required iff fulfillmentType is DOMAIN_TRANSFER, forbidden otherwise — enforced below and
     *  backstopped by a DB CHECK constraint (see the add_domain_transfers migration). Unlike
     *  accountHandle, this drives a real publish gate: services/domainVerification.ts's DNS TXT
     *  ownership check must pass before a DOMAIN_TRANSFER listing can be published. */
    domainName: domainNameSchema.optional(),
    /** Required (together with nftTokenId) iff fulfillmentType is NFT_TRANSFER, forbidden otherwise
     *  — enforced below and backstopped by a DB CHECK constraint (see the add_nft_transfers
     *  migration). nftChainId is never client-supplied — v1 only supports the platform's own chain
     *  (BASE_CHAIN_ID), set server-side at creation (see services/listings.ts). Unlike domainName,
     *  there's no separate verification-token round trip: lib/nftVerification.ts's ownerOf read is
     *  live, so verification is just "call it and see" against the seller's own on-file wallet. */
    nftContractAddress: addressSchema.optional(),
    nftTokenId: nftTokenIdSchema.optional(),
});
export const createListingRequestSchema = createListingRequestBaseSchema
    .refine((data) => data.fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
    ? data.accountPlatform !== undefined
    : data.accountPlatform === undefined, {
    message: "accountPlatform is required when fulfillmentType is ACCOUNT_TRANSFER, and must be omitted otherwise",
    path: ["accountPlatform"],
})
    .refine((data) => data.fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER
    ? data.domainName !== undefined
    : data.domainName === undefined, {
    message: "domainName is required when fulfillmentType is DOMAIN_TRANSFER, and must be omitted otherwise",
    path: ["domainName"],
})
    .refine((data) => data.fulfillmentType === ListingFulfillmentType.NFT_TRANSFER
    ? data.nftContractAddress !== undefined && data.nftTokenId !== undefined
    : data.nftContractAddress === undefined && data.nftTokenId === undefined, {
    message: "nftContractAddress and nftTokenId are both required when fulfillmentType is NFT_TRANSFER, and must be omitted otherwise",
    path: ["nftContractAddress"],
})
    .refine((data) => !(data.category === ListingCategory.GAME_ACCOUNTS &&
    (data.fulfillmentType === ListingFulfillmentType.FILE ||
        data.fulfillmentType === ListingFulfillmentType.LICENSE_KEY)), {
    // GAME_ACCOUNTS covers Steam/Epic/PSN/Xbox/Riot and similar — platforms whose ToS explicitly
    // prohibits account sales (see MARKETPLACE_EXPANSION_PLAN.md's Phase 2). FILE and LICENSE_KEY
    // fulfillment can carry arbitrary credential-shaped content with zero platform restriction,
    // which would let a GAME_ACCOUNTS listing become a backdoor around that deliberate decision —
    // ACCOUNT_TRANSFER is the only fulfillment type that actually gates which platforms are
    // supported (see AccountPlatform), so it's the only one this category may use.
    message: "GAME_ACCOUNTS listings can't use FILE or LICENSE_KEY fulfillment — those platforms' account sales aren't supported (see MARKETPLACE_EXPANSION_PLAN.md)",
    path: ["category"],
});
export const updateListingRequestSchema = z.object({
    title: z.string().trim().min(3).max(140).optional(),
    description: z.string().trim().min(1).max(10_000).optional(),
    priceBaseUnits: positiveBigintBaseUnitsSchema.optional(),
    category: listingCategorySchema.optional(),
    // fulfillmentType/accountPlatform are deliberately not editable here — see services/listings.ts'
    // "immutable after creation" note on fulfillmentType; accountPlatform is locked to it.
    // accountHandle IS editable (e.g. a seller fixing a typo) — see createListingRequestBaseSchema's
    // docblock for why it's optional even for ACCOUNT_TRANSFER. Re-verifies stats when it changes.
    accountHandle: z.string().trim().min(1).max(100).optional(),
    // domainName IS editable too (e.g. a typo before verification) — changing it resets
    // domainOwnershipVerifiedAt to null, since the DNS TXT challenge was for the old domain.
    domainName: domainNameSchema.optional(),
    // Same pre-publish-editable/post-publish-frozen treatment as domainName — changing either resets
    // nftOwnershipVerifiedAt, since the old verification only proved something about the old
    // contract/token. Edited together: services/listings.ts treats a change to either as "changed".
    nftContractAddress: addressSchema.optional(),
    nftTokenId: nftTokenIdSchema.optional(),
});
export const listingResponseSchema = z.object({
    id: uuidSchema,
    sellerId: uuidSchema,
    /** The seller's public display name, or null if they haven't set one. Never their email. */
    sellerUsername: z.string().nullable(),
    /** Aggregated across every SellerReview for this listing's seller — not scoped to this
     *  listing, since a rating reflects trust in the seller, not any one item. */
    sellerRating: sellerRatingSummarySchema,
    /** Server-computed, never self-declared — see apps/api/src/services/badges.ts. */
    sellerBadges: z.array(sellerBadgeSchema),
    title: z.string(),
    description: z.string(),
    /** True when the seller uploaded a cover image; fetch it from GET /listings/:id/cover (use
     *  `updatedAt` as a cache-busting query value). */
    hasCoverImage: z.boolean(),
    priceBaseUnits: bigintBaseUnitsSchema,
    category: listingCategorySchema,
    fulfillmentType: listingFulfillmentTypeSchema,
    /** Only meaningful for a LICENSE_KEY listing — null for FILE, since stock doesn't apply there.
     *  A published LICENSE_KEY listing at 0 is "sold out" but stays visible/restockable. */
    availableLicenseKeyCount: z.number().int().nonnegative().nullable(),
    /** Only meaningful (non-null) for an ACCOUNT_TRANSFER listing — see the AccountPlatform
     *  docblock. Drives the platform-specific handoff guidance shown in the order flow. */
    accountPlatform: accountPlatformSchema.nullable(),
    /** Seller-supplied public handle/username, if any — see services/platformStats.ts. */
    accountHandle: z.string().nullable(),
    accountStatsFollowerCount: z.number().int().nonnegative().nullable(),
    accountStatsAccountCreatedAt: z.string().datetime().nullable(),
    /** Non-null only on a successful stats fetch — this, not the stat fields themselves, is what a
     *  "platform-verified" badge should actually check (a zero-follower fetch is still verified). */
    accountStatsFetchedAt: z.string().datetime().nullable(),
    /** Only meaningful (non-null) for a DOMAIN_TRANSFER listing. */
    domainName: z.string().nullable(),
    /** The token the seller must publish in a DNS TXT record to prove control — see
     *  services/domainVerification.ts. Not secret; only proves anything alongside actual DNS
     *  control, so there's no harm in it appearing on the public listing response. */
    domainVerificationToken: z.string().nullable(),
    /** Non-null only once the TXT-record challenge has actually passed — required before a
     *  DOMAIN_TRANSFER listing can be published (see services/listings.ts' publishListing). */
    domainOwnershipVerifiedAt: z.string().datetime().nullable(),
    /** Only meaningful (non-null) for an NFT_TRANSFER listing — see services/nftTransfers.ts. */
    nftContractAddress: z.string().nullable(),
    nftTokenId: z.string().nullable(),
    nftChainId: z.number().int().nullable(),
    /** Non-null only once a listing-time ownerOf(tokenId) read confirmed the seller's own on-file
     *  wallet holds the token — required before an NFT_TRANSFER listing can be published (see
     *  services/listings.ts' publishListing). Unlike domain verification, re-checkable instantly
     *  (no propagation delay) — see lib/nftVerification.ts. */
    nftOwnershipVerifiedAt: z.string().datetime().nullable(),
    status: z.enum([
        ListingStatus.DRAFT,
        ListingStatus.SCANNING,
        ListingStatus.READY,
        ListingStatus.PUBLISHED,
        ListingStatus.REJECTED,
        ListingStatus.ARCHIVED,
    ]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listingSortSchema = z.enum(["NEWEST", "PRICE_ASC", "PRICE_DESC"]);
/** Query params for `GET /listings` — browse/search/filter, all optional. `q` runs a Postgres
 *  full-text match (see services/listings.ts) rather than a plain substring `LIKE`, so it
 *  tolerates stemming, word order, and multi-word queries. Cursor/limit are inlined rather than
 *  extending `paginationQuerySchema` (defined later in this file) — same convention every other
 *  paginated-query schema here already follows. */
export const listListingsQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    category: listingCategorySchema.optional(),
    minPriceBaseUnits: bigintBaseUnitsSchema.optional(),
    maxPriceBaseUnits: bigintBaseUnitsSchema.optional(),
    sort: listingSortSchema.default("NEWEST"),
    q: z.string().trim().min(1).max(200).optional(),
    /** Scopes the browse feed to one seller's published listings — powers the public seller
     *  storefront page (see routes/sellers.ts) by reusing this endpoint rather than a second
     *  listing-fetch implementation. */
    sellerId: uuidSchema.optional(),
});
/** Public seller storefront — see routes/sellers.ts. Deliberately excludes any avatar/email-
 *  derived image: gravatarUrl is only ever wired to the account's own owner (see its docblock)
 *  since it's a stable, cross-service, email-derived identifier — handing it to every visitor of
 *  a public page would be exactly the kind of third-party correlation that constraint exists to
 *  prevent. */
export const sellerProfileResponseSchema = z.object({
    sellerId: uuidSchema,
    username: z.string().nullable(),
    memberSince: z.string().datetime(),
    sellerRating: sellerRatingSummarySchema,
    sellerBadges: z.array(sellerBadgeSchema),
});
export const presignUploadRequestSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    declaredMimeType: z.string().trim().min(1).max(255),
    sizeBytes: z.coerce.number().int().positive(),
});
export const presignUploadResponseSchema = z.object({
    uploadId: uuidSchema,
    url: z.string().url(),
    method: z.enum(["POST", "PUT"]),
    fields: z.record(z.string()).optional(),
    headers: z.record(z.string()).optional(),
    expiresAt: z.string().datetime(),
    maxSizeBytes: z.number().int().positive(),
});
export const completeUploadRequestSchema = z.object({
    etag: z.string().optional(),
});
export const uploadResponseSchema = z.object({
    id: uuidSchema,
    listingId: uuidSchema,
    displayFilename: z.string(),
    declaredMimeType: z.string(),
    detectedMimeType: z.string().nullable(),
    sizeBytes: z.number(),
    status: z.enum([
        UploadStatus.PRESIGNED,
        UploadStatus.UPLOADED,
        UploadStatus.SCANNING,
        UploadStatus.CLEAN,
        UploadStatus.INFECTED,
        UploadStatus.FAILED,
        UploadStatus.DELETED,
    ]),
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// License-key fulfillment — seller-only stock management. See services/licenseKeys.ts.
// ---------------------------------------------------------------------------
export const addLicenseKeysRequestSchema = z.object({
    /** One entry per code — the client splits pasted text on newlines before sending. */
    codes: z.array(z.string().trim().min(1).max(2000)).min(1).max(500),
});
const licenseKeyStatusEnum = z.enum([LicenseKeyStatus.AVAILABLE, LicenseKeyStatus.CLAIMED]);
export const licenseKeyResponseSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    status: licenseKeyStatusEnum,
    createdAt: z.string().datetime(),
    claimedAt: z.string().datetime().nullable(),
});
/** The buyer's own reveal of the one code their order claimed — never any other key. */
export const revealedLicenseKeyResponseSchema = z.object({
    code: z.string(),
    claimedAt: z.string().datetime().nullable(),
});
// ---------------------------------------------------------------------------
// Account-transfer fulfillment — a manual, admin-mediated custodial handoff, not a self-service
// stock like license keys. See services/accountTransfers.ts for the full design.
// ---------------------------------------------------------------------------
/** Buyer-only, YOUTUBE-platform orders only — the Google account email to add as a Brand Account
 *  owner. Captured early (right after funding) so the seller can invite the buyer as an owner the
 *  same day they invite the platform, rather than a several-day-later relay: any current Owner can
 *  invite another immediately, so both the platform and the buyer can start their 7-day
 *  Owner-since clock on day 0, instead of the buyer's clock only starting once the platform gets
 *  around to relaying it. Editable while still AWAITING_SELLER_HANDOFF or PLATFORM_VERIFYING (a
 *  typo fix), locked once READY_FOR_BUYER — by then the seller may already have sent the invite to
 *  the old address. */
export const submitAccountTransferBuyerEmailRequestSchema = z.object({
    email: z.string().trim().email().max(320),
});
/** Admin-only — advances an ACCOUNT_TRANSFER order out of AWAITING_SELLER_HANDOFF once the
 *  platform has taken custody of the account from the seller. The later transitions
 *  (READY_FOR_BUYER, CREDENTIALS_REVEALED) aren't driven by this endpoint — see
 *  submitAccountTransferCredentialsRequestSchema and the buyer-facing reveal endpoint. */
export const advanceAccountTransferStageRequestSchema = z.object({
    stage: z.literal(AccountTransferStage.PLATFORM_VERIFYING),
});
/** The seller's own submission of their account to Wicked Escrow's custody — the other half of
 *  the handoff from submitAccountTransferCredentialsRequestSchema below (that one is the platform
 *  handing the *buyer* their final credentials; this one is the *seller* handing the platform the
 *  account in the first place). Same shape and same "at least one field" rule: for YouTube, where
 *  the real handoff is granting Brand-Account "Owner" access directly on Google's side rather than
 *  sharing a password, `notes` carries the seller's confirmation instead of real credentials.
 *  Submitting this auto-advances the order out of AWAITING_SELLER_HANDOFF into PLATFORM_VERIFYING
 *  — no separate admin click needed to unblock verification. */
export const submitAccountTransferSellerHandoffRequestSchema = z
    .object({
    username: z.string().trim().max(200).optional(),
    password: z.string().max(500).optional(),
    notes: z.string().trim().max(4000).optional(),
})
    .refine((data) => Boolean(data.username || data.password || data.notes), {
    message: "At least one of username, password, or notes is required",
});
/** Admin-only — the decrypted view of what the seller submitted, shown while an admin verifies
 *  the account before resetting it and handing final credentials to the buyer. */
export const accountTransferSellerHandoffResponseSchema = z.object({
    username: z.string().nullable(),
    password: z.string().nullable(),
    notes: z.string().nullable(),
    submittedAt: z.string().datetime(),
});
/** Admin-only — the final, buyer-facing credentials for an ACCOUNT_TRANSFER order, entered once
 *  the platform has reset them away from the seller. At least one of username/password/notes
 *  must carry something, but which fields matter varies by platform (e.g. a YouTube transfer may
 *  need only notes — "you're now Primary Owner" — while an X/TikTok transfer needs real
 *  credentials) so none is individually required. */
export const submitAccountTransferCredentialsRequestSchema = z
    .object({
    username: z.string().trim().max(200).optional(),
    password: z.string().max(500).optional(),
    notes: z.string().trim().max(4000).optional(),
})
    .refine((data) => Boolean(data.username || data.password || data.notes), {
    message: "At least one of username, password, or notes is required",
});
/** The buyer's own one-time reveal of their order's final account credentials — mirrors
 *  revealedLicenseKeyResponseSchema's shape/intent exactly. */
export const revealedAccountTransferCredentialsResponseSchema = z.object({
    username: z.string().nullable(),
    password: z.string().nullable(),
    notes: z.string().nullable(),
    revealedAt: z.string().datetime(),
});
/** One row in the admin account-transfer queue (GET /admin/account-transfers) — see
 *  services/accountTransfers.ts' AccountTransferQueueItem, which this mirrors exactly. */
export const accountTransferQueueItemSchema = z.object({
    id: uuidSchema,
    orderId: bytes32Schema,
    listingTitle: z.string(),
    accountPlatform: accountPlatformSchema.nullable(),
    accountTransferStage: accountTransferStageSchema,
    buyerUsername: z.string().nullable(),
    sellerUsername: z.string().nullable(),
    financialStatus: z.string(),
    createdAt: z.string().datetime(),
    /** Whether the seller has submitted their handoff yet — lets the admin queue distinguish
     *  "still waiting on the seller" from "seller submitted, needs review" without a separate
     *  fetch per row. */
    sellerHandoffSubmitted: z.boolean(),
    /** Null once settled (RELEASED/REFUNDED); otherwise the same deadline the buyer's own order
     *  page shows — lets the admin queue flag orders running out of runway before the buyer's
     *  dispute window closes on an account that was never delivered. */
    disputeDeadline: z.string().datetime().nullable(),
    /** YOUTUBE orders only — the buyer's submitted Google email, so the admin knows what to tell
     *  the seller to invite. Always null for every other platform. */
    buyerGoogleEmail: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Domain-transfer fulfillment — a single seller-submits/buyer-reveals handoff, not a staged
// custodial one. See services/domainTransfers.ts.
// ---------------------------------------------------------------------------
/** Seller-only — the domain's real transfer/EPP code (whatever the registrar calls it), plus
 *  optional notes. `code` is required (unlike the account-transfer credential fields, there's no
 *  "notes-only" case here — a domain transfer always needs the actual code). Submitting this
 *  auto-advances the order to READY_FOR_BUYER; no separate admin verification step exists — see
 *  DomainTransferStage's docblock for why. */
// Field named transferCode, not the generic "code" — that would collide with structured-log
// redaction (ApiError.code, e.g. "NOT_FOUND"/"VALIDATION_FAILED", legitimately appears in error
// logs and must never be masked; see apps/api/src/lib/logger.ts's REDACT_PATHS, which redacts
// "*.transferCode" precisely because this name doesn't collide with anything else).
export const submitDomainTransferCodeRequestSchema = z.object({
    transferCode: z.string().trim().min(1).max(500),
    notes: z.string().trim().max(4000).optional(),
});
/** The buyer's own one-time reveal of their order's transfer code — mirrors
 *  revealedAccountTransferCredentialsResponseSchema/revealedLicenseKeyResponseSchema exactly. */
export const revealedDomainTransferCodeResponseSchema = z.object({
    transferCode: z.string(),
    notes: z.string().nullable(),
    revealedAt: z.string().datetime(),
});
/** Admin-only — the decrypted transfer code, for dispute support only (there's no "verify" action
 *  to take on it, unlike the account-transfer seller-handoff view). */
export const domainTransferCredentialResponseSchema = z.object({
    transferCode: z.string(),
    notes: z.string().nullable(),
    submittedAt: z.string().datetime(),
    revealedAt: z.string().datetime().nullable(),
});
/** One row in the admin domain-transfer queue (GET /admin/domain-transfers) — mirrors
 *  accountTransferQueueItemSchema, minus the fields with no domain-transfer equivalent
 *  (sellerHandoffSubmitted/buyerGoogleEmail are account-transfer-specific). */
export const domainTransferQueueItemSchema = z.object({
    id: uuidSchema,
    orderId: bytes32Schema,
    listingTitle: z.string(),
    domainName: z.string().nullable(),
    domainTransferStage: domainTransferStageSchema,
    buyerUsername: z.string().nullable(),
    sellerUsername: z.string().nullable(),
    financialStatus: z.string(),
    createdAt: z.string().datetime(),
    disputeDeadline: z.string().datetime().nullable(),
});
// ---------------------------------------------------------------------------
// NFT-transfer fulfillment — direct peer-to-peer, no custody, no encryption. See
// services/nftTransfers.ts and MARKETPLACE_EXPANSION_PLAN.md's Phase 5a'.
// ---------------------------------------------------------------------------
/** Response from either party's "check delivery now" action — a live on-chain ownerOf read, no
 *  propagation delay to wait out (unlike domain DNS verification). `delivered` reflects the
 *  result of *this* check; the order's own nftTransferStage (in OrderResponse) is what persists
 *  across checks. */
export const nftTransferCheckResponseSchema = z.object({
    delivered: z.boolean(),
    checkedAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// In-app notifications — see packages/database's Notification model docblock.
// ---------------------------------------------------------------------------
const notificationTypeEnum = z.enum([
    NotificationType.MESSAGE_RECEIVED,
    NotificationType.OFFER_RECEIVED,
    NotificationType.OFFER_COUNTERED,
    NotificationType.OFFER_ACCEPTED,
    NotificationType.OFFER_DECLINED,
    NotificationType.TICKET_REPLY,
    NotificationType.WATCHLIST_PRICE_DROP,
    NotificationType.SAVED_SEARCH_MATCH,
    NotificationType.ORDER_FUNDED,
    NotificationType.ORDER_RELEASED,
    NotificationType.ORDER_DISPUTED,
    NotificationType.DISPUTE_RESOLVED,
    NotificationType.ACCOUNT_TRANSFER_READY,
    NotificationType.ACCOUNT_TRANSFER_DEADLINE_APPROACHING,
    NotificationType.ACCOUNT_TRANSFER_DEADLINE_URGENT,
    NotificationType.DOMAIN_TRANSFER_READY,
    NotificationType.NFT_TRANSFER_DELIVERED,
]);
export const notificationResponseSchema = z.object({
    id: uuidSchema,
    type: notificationTypeEnum,
    title: z.string(),
    body: z.string(),
    linkPath: z.string().nullable(),
    readAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/** The only types a user may opt out of. Anything touching money, an order, a dispute, a transfer
 *  deadline, or a support reply stays on — missing one can cost someone funds or a deadline. */
export const MUTABLE_NOTIFICATION_TYPES = [
    NotificationType.MESSAGE_RECEIVED,
    NotificationType.OFFER_RECEIVED,
    NotificationType.OFFER_COUNTERED,
    NotificationType.OFFER_ACCEPTED,
    NotificationType.OFFER_DECLINED,
    NotificationType.WATCHLIST_PRICE_DROP,
    NotificationType.SAVED_SEARCH_MATCH,
];
export const NOTIFICATION_TYPE_LABELS = {
    MESSAGE_RECEIVED: "New messages",
    OFFER_RECEIVED: "Offers on my listings",
    OFFER_COUNTERED: "Counter-offers",
    OFFER_ACCEPTED: "Accepted offers",
    OFFER_DECLINED: "Declined offers",
    WATCHLIST_PRICE_DROP: "Price drops on my watchlist",
    SAVED_SEARCH_MATCH: "New listings matching my saved searches",
};
const mutableNotificationTypeEnum = z.enum(MUTABLE_NOTIFICATION_TYPES);
export const notificationPreferencesSchema = z.object({
    emailAlertsEnabled: z.boolean(),
    mutedNotificationTypes: z.array(mutableNotificationTypeEnum),
});
export const updateNotificationPreferencesRequestSchema = notificationPreferencesSchema.partial();
/** The user must type DELETE — the one step that stops a stray click erasing an account. */
export const deleteAccountRequestSchema = z.object({
    confirm: z.literal("DELETE"),
});
export const notificationsUnreadCountResponseSchema = z.object({
    count: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Watchlist — a buyer tracking one listing for a price drop. See services/watchlist.ts.
// ---------------------------------------------------------------------------
export const watchlistEntryResponseSchema = z.object({
    id: uuidSchema,
    listing: listingResponseSchema,
    /** The price the watcher was last emailed about (seeded to the listing's price at watch-time).
     *  Only exposed so the UI can show "was $X" alongside the current price. */
    lastNotifiedPriceBaseUnits: bigintBaseUnitsSchema,
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Saved searches — a standing browse-filter combination the user is emailed about when a newly
// published listing matches. See services/savedSearches.ts.
// ---------------------------------------------------------------------------
export const createSavedSearchRequestSchema = z
    .object({
    label: z.string().trim().min(1).max(100),
    q: z.string().trim().min(1).max(200).optional(),
    category: listingCategorySchema.optional(),
    maxPriceBaseUnits: bigintBaseUnitsSchema.optional(),
})
    .refine((v) => Boolean(v.q) || Boolean(v.category) || Boolean(v.maxPriceBaseUnits), {
    message: "Add at least one filter (keyword, category, or max price) to save this search",
});
export const savedSearchResponseSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    q: z.string().nullable(),
    category: listingCategorySchema.nullable(),
    maxPriceBaseUnits: bigintBaseUnitsSchema.nullable(),
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Best Offer negotiation — see services/offers.ts. Separate from checkout: an offer only ever
// produces a negotiated *price*; buying still goes through the normal checkout-intent flow with
// that offer's id attached (see `createCheckoutIntentRequestSchema` below).
// ---------------------------------------------------------------------------
export const createOfferRequestSchema = z.object({
    amountBaseUnits: positiveBigintBaseUnitsSchema,
});
export const respondToOfferRequestSchema = z.object({
    amountBaseUnits: positiveBigintBaseUnitsSchema,
});
const offerStatusEnum = z.enum([
    OfferStatus.PENDING,
    OfferStatus.COUNTERED,
    OfferStatus.ACCEPTED,
    OfferStatus.DECLINED,
    OfferStatus.EXPIRED,
]);
export const offerResponseSchema = z.object({
    id: uuidSchema,
    listingId: uuidSchema,
    listingTitle: z.string(),
    listingPriceBaseUnits: bigintBaseUnitsSchema,
    buyerId: uuidSchema,
    buyerUsername: z.string().nullable(),
    sellerId: uuidSchema,
    sellerUsername: z.string().nullable(),
    status: offerStatusEnum,
    currentAmountBaseUnits: bigintBaseUnitsSchema,
    currentProposerRole: z.enum([OfferProposerRole.BUYER, OfferProposerRole.SELLER]),
    roundCount: z.number().int().positive(),
    /** True once accepted-but-not-yet-checked-out — lets the accepting side's UI show "ready to
     *  check out" vs. an already-consumed accepted offer. */
    canCheckout: z.boolean(),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listOffersQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
// ---------------------------------------------------------------------------
// Checkout / orders
// ---------------------------------------------------------------------------
export const createCheckoutIntentRequestSchema = z
    .object({
    listingId: uuidSchema,
    /** Present only when checking out at a negotiated Best Offer price — see services/offers.ts.
     *  The offer must be ACCEPTED, belong to this listing and to the caller as buyer, and not
     *  already used by a prior order. */
    offerId: uuidSchema.optional(),
    /** A first-purchase coupon code — see services/coupons.ts. Discounts the listing's standing
     *  price, so it can't be combined with a negotiated offer price. */
    couponCode: z.string().trim().min(1).max(40).optional(),
    idempotencyKey: z.string().min(1).max(255),
})
    .refine((v) => !(v.offerId && v.couponCode), {
    message: "Can't combine a coupon code with a negotiated offer",
    path: ["couponCode"],
});
export const contractCallSchema = z.object({
    to: addressSchema,
    data: z.string().regex(/^0x[a-fA-F0-9]*$/),
    value: bigintBaseUnitsSchema,
});
// ---------------------------------------------------------------------------
// Onramp
// ---------------------------------------------------------------------------
/** A ready-to-use, single-use Coinbase Onramp URL, already scoped to the caller's own wallet
 *  address server-side — see apps/api/src/lib/onramp.ts. */
export const onrampSessionResponseSchema = z.object({
    onrampUrl: z.string().url(),
});
export const checkoutIntentResponseSchema = z.object({
    orderId: bytes32Schema,
    internalOrderId: uuidSchema,
    listingId: uuidSchema,
    sellerAddress: addressSchema,
    buyerAddress: addressSchema,
    principalBaseUnits: bigintBaseUnitsSchema,
    platformFeeBps: z.number().int().positive(),
    platformFeeBaseUnits: bigintBaseUnitsSchema,
    totalDueBaseUnits: bigintBaseUnitsSchema,
    /** Non-zero only when a coupon was applied — see services/coupons.ts. Already reflected in
     *  `principalBaseUnits` above (the listing's standing price minus this); broken out separately
     *  so the checkout UI can show "Discount: -$X" as its own line. */
    discountBaseUnits: bigintBaseUnitsSchema,
    couponCode: z.string().nullable(),
    treasuryAddress: addressSchema,
    chainId: z.number().int().positive(),
    usdcContractAddress: addressSchema,
    escrowContractAddress: addressSchema,
    disputeDeadlineSeconds: z.number().int().positive(),
    nonrefundableFeeNotice: z.string(),
    calls: z.object({
        approve: contractCallSchema,
        fundEscrow: contractCallSchema,
    }),
});
export const submitCheckoutIntentRequestSchema = z.object({
    userOperationHash: z.string().optional(),
    transactionHash: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Seller reviews
// ---------------------------------------------------------------------------
export const createReviewRequestSchema = z.object({
    rating: z.number().int().min(1).max(5),
    comment: z.string().trim().max(1_000).optional(),
});
export const sellerReviewResponseSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    sellerId: uuidSchema,
    reviewerId: uuidSchema,
    /** The reviewer's public display name, or null if they haven't set one. */
    reviewerUsername: z.string().nullable(),
    rating: z.number().int().min(1).max(5),
    comment: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const orderResponseSchema = z.object({
    id: uuidSchema,
    orderId: bytes32Schema,
    listingId: uuidSchema,
    listingTitle: z.string(),
    listingFulfillmentType: listingFulfillmentTypeSchema,
    /** Only non-null when listingFulfillmentType is ACCOUNT_TRANSFER — denormalized here (same
     *  pattern as listingTitle/listingFulfillmentType) so the order page can show
     *  platform-specific handoff guidance without a second round trip. */
    accountPlatform: accountPlatformSchema.nullable(),
    buyerId: uuidSchema,
    sellerId: uuidSchema,
    /** The counterparty's public display name — sellerUsername for a buyer viewing their own
     *  order, buyerUsername for a seller viewing theirs. Both are exposed since either party may
     *  view this response; the frontend only ever shows the one relevant to who's looking. */
    buyerUsername: z.string().nullable(),
    sellerUsername: z.string().nullable(),
    buyerWalletId: uuidSchema,
    sellerWalletId: uuidSchema,
    /** Present only when this order was checked out at a negotiated Best Offer price rather than
     *  the listing's standing price. */
    offerId: uuidSchema.nullable(),
    /** "0" unless a first-purchase coupon was redeemed for this order — see services/coupons.ts. */
    discountBaseUnits: bigintBaseUnitsSchema,
    couponCode: z.string().nullable(),
    /** Only non-null for an order against an ACCOUNT_TRANSFER listing — see
     *  services/accountTransfers.ts and the AccountTransferStage docblock. */
    accountTransferStage: accountTransferStageSchema.nullable(),
    /** Always false for a non-ACCOUNT_TRANSFER order. Lets the seller's own order page distinguish
     *  "you haven't submitted the account yet" from "already submitted, waiting on the platform" —
     *  the stage alone can't, since PLATFORM_VERIFYING is also reachable via an admin's manual
     *  override with no seller submission at all. */
    sellerHandoffSubmitted: z.boolean(),
    /** When the seller submitted their handoff — null until they have. YOUTUBE-only use: anchors
     *  the buyer's own 7-day Google-enforced wait to become Primary Owner, since the seller invites
     *  the platform and the buyer as Owner the same day (see AccountTransferPanel's countdown). This
     *  is our own submission timestamp, not a verified fact about when Google's clock actually
     *  started — an estimate is still more useful to a buyer than no countdown at all. */
    sellerHandoffSubmittedAt: z.string().nullable(),
    /** YOUTUBE-platform orders only — the buyer's submitted Google account email, so the seller
     *  knows who to invite as an Owner. Always null for every other platform or before the buyer
     *  has submitted it. */
    buyerGoogleEmail: z.string().nullable(),
    /** Only non-null for an order against a DOMAIN_TRANSFER listing — see
     *  services/domainTransfers.ts and the DomainTransferStage docblock. */
    domainTransferStage: domainTransferStageSchema.nullable(),
    /** Only non-null for an order against an NFT_TRANSFER listing — see
     *  services/nftTransfers.ts and the NftTransferStage docblock. */
    nftTransferStage: nftTransferStageSchema.nullable(),
    /** NFT_TRANSFER orders only — the buyer's own funding-wallet address, i.e. exactly where the
     *  seller must send the NFT (directly, never to the platform — see SECURITY.md's non-custodial
     *  invariant). Denormalized here the same way accountPlatform/domainTransferStage are, so the
     *  seller doesn't need a second lookup to find the delivery address. Null for every other
     *  fulfillment type. */
    nftDeliveryAddress: addressSchema.nullable(),
    /** Last time either party triggered an on-chain "check delivery now" read. Null until the
     *  first check. NFT_TRANSFER orders only. */
    nftDeliveryCheckedAt: z.string().datetime().nullable(),
    /** Present once this order has a seller review (RELEASED orders only, one per order). Lets
     *  the buyer's own order page know whether "leave a review" or the existing review applies. */
    review: sellerReviewResponseSchema.nullable(),
    principalBaseUnits: bigintBaseUnitsSchema,
    platformFeeBaseUnits: bigintBaseUnitsSchema,
    totalChargedBaseUnits: bigintBaseUnitsSchema,
    financialStatus: z.enum([
        FinancialStatus.PENDING_FUNDING,
        FinancialStatus.FUNDED,
        FinancialStatus.DISPUTED,
        FinancialStatus.RELEASED,
        FinancialStatus.REFUNDED,
        FinancialStatus.EXPIRED,
        FinancialStatus.FAILED,
    ]),
    deliveryStatus: z.enum([
        DeliveryStatus.NOT_ENTITLED,
        DeliveryStatus.AVAILABLE,
        DeliveryStatus.DOWNLOADED,
        DeliveryStatus.REVOKED,
    ]),
    disputeDeadline: z.string().datetime().nullable(),
    fundingTransactionHash: z.string().nullable(),
    releaseTransactionHash: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const downloadResponseSchema = z.object({
    url: z.string().url(),
    expiresAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Disputes
// ---------------------------------------------------------------------------
export const createDisputeRequestSchema = z.object({
    category: z.string().trim().min(1).max(100),
    claim: z.string().trim().min(1).max(5_000),
    evidenceUploadIds: z.array(uuidSchema).max(10).default([]),
});
export const disputeResponseSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    status: z.enum([
        DisputeStatus.PENDING_ONCHAIN,
        DisputeStatus.OPEN,
        DisputeStatus.UNDER_REVIEW,
        DisputeStatus.RESOLVED,
        DisputeStatus.REJECTED,
    ]),
    category: z.string(),
    claim: z.string(),
    sellerRebuttal: z.string().nullable(),
    createdAt: z.string().datetime(),
    resolvedAt: z.string().datetime().nullable(),
    /** Present only on the create-dispute response: the typed openDispute() call for the buyer to
     *  sign. evidenceHash is a server-computed commitment tied to this dispute's id, not client
     *  input — see apps/api services/disputes.ts. */
    openDisputeCall: contractCallSchema.optional(),
});
export const rebuttalRequestSchema = z.object({
    rebuttal: z.string().trim().min(1).max(5_000),
});
/** MODERATOR/ADMIN only — see `GET /api/v1/disputes`. */
export const listDisputesQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z
        .enum([
        DisputeStatus.PENDING_ONCHAIN,
        DisputeStatus.OPEN,
        DisputeStatus.UNDER_REVIEW,
        DisputeStatus.RESOLVED,
        DisputeStatus.REJECTED,
    ])
        .optional(),
});
// ---------------------------------------------------------------------------
// Buyer/seller chat
// ---------------------------------------------------------------------------
export const createMessageRequestSchema = z.object({
    body: z.string().trim().min(1).max(2_000),
});
export const messageResponseSchema = z.object({
    id: uuidSchema,
    conversationId: uuidSchema,
    senderId: uuidSchema,
    senderUsername: z.string().nullable(),
    body: z.string(),
    createdAt: z.string().datetime(),
});
export const conversationResponseSchema = z.object({
    id: uuidSchema,
    listingId: uuidSchema,
    listingTitle: z.string(),
    buyerId: uuidSchema,
    buyerUsername: z.string().nullable(),
    sellerId: uuidSchema,
    sellerUsername: z.string().nullable(),
    createdAt: z.string().datetime(),
    lastMessageAt: z.string().datetime(),
});
/** Not cursor-paginated — a conversation thread is fetched as a bounded "last N messages"
 *  window (see apps/api's chatRouter), not infinite-scrolled. */
export const messagesListResponseSchema = z.object({
    items: z.array(messageResponseSchema),
});
// ---------------------------------------------------------------------------
// Coupons — admin-managed first-purchase discount codes. See services/coupons.ts for why the
// discount comes out of the seller's proceeds and is capped low.
// ---------------------------------------------------------------------------
export const createCouponRequestSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(3)
        .max(40)
        .regex(/^[A-Z0-9-]+$/, "letters, numbers, and hyphens only"),
    /** Basis points off the principal (1000 = 10%). Capped server-side at 20% regardless of what's
     *  sent here — see MAX_COUPON_DISCOUNT_BPS. */
    discountBps: z.number().int().positive().max(2000),
    maxRedemptions: z.number().int().positive().optional(),
    expiresAt: z.string().datetime().optional(),
});
export const couponResponseSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    discountBps: z.number().int(),
    maxRedemptions: z.number().int().nullable(),
    redemptionCount: z.number().int(),
    expiresAt: z.string().datetime().nullable(),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Blog — deliberately not a CMS. See packages/database's Post model docblock and
// services/posts.ts.
// ---------------------------------------------------------------------------
export const postStatusSchema = z.enum([PostStatus.DRAFT, PostStatus.PUBLISHED]);
export const postSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(120)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "lowercase letters, numbers, and single hyphens only");
export const createPostRequestSchema = z.object({
    title: z.string().trim().min(3).max(200),
    /** Auto-generated from the title if omitted — see services/posts.ts. Only needed here for an
     *  admin who wants a specific URL rather than whatever the title would produce. */
    slug: postSlugSchema.optional(),
    excerpt: z.string().trim().max(400).optional(),
    bodyHtml: z.string().trim().min(1),
});
export const updatePostRequestSchema = z.object({
    title: z.string().trim().min(3).max(200).optional(),
    slug: postSlugSchema.optional(),
    excerpt: z.string().trim().max(400).optional(),
    bodyHtml: z.string().trim().min(1).optional(),
});
export const postResponseSchema = z.object({
    id: uuidSchema,
    slug: z.string(),
    title: z.string(),
    excerpt: z.string().nullable(),
    bodyHtml: z.string(),
    status: postStatusSchema,
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** The public /blog list only ever needs enough to render a card — omitting bodyHtml keeps that
 *  response small regardless of how long individual posts get. */
export const postSummaryResponseSchema = postResponseSchema.omit({ bodyHtml: true });
// ---------------------------------------------------------------------------
// Admin dashboard — platform-operator surface, wholly separate from the CDP-authenticated
// end-user identity system (see apps/api/src/lib/adminAuth.ts). None of these schemas are ever
// reachable without a valid admin session token.
// ---------------------------------------------------------------------------
export const adminLoginRequestSchema = z.object({
    username: z.string().min(1),
    password: z.string().min(1),
});
export const adminSessionResponseSchema = z.object({
    token: z.string(),
    expiresAt: z.string().datetime(),
});
export const adminUserSummarySchema = z.object({
    id: uuidSchema,
    username: z.string().nullable(),
    email: z.string().nullable(),
    status: z.enum([UserStatus.ACTIVE, UserStatus.SUSPENDED]),
    role: z.enum([UserRole.USER, UserRole.MODERATOR, UserRole.ADMIN]),
    createdAt: z.string().datetime(),
    listingCount: z.number().int().nonnegative(),
    buyerOrderCount: z.number().int().nonnegative(),
    sellerOrderCount: z.number().int().nonnegative(),
});
export const adminWalletSchema = z.object({
    id: uuidSchema,
    checksumAddress: addressSchema,
    chainId: z.number().int().positive(),
    walletType: z.enum([WalletType.EOA, WalletType.SMART_ACCOUNT]),
    isActive: z.boolean(),
    createdAt: z.string().datetime(),
});
export const adminUserDetailSchema = adminUserSummarySchema.extend({
    wallets: z.array(adminWalletSchema),
    sellerRating: sellerRatingSummarySchema,
});
export const adminListUsersQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    /** Matches against username or email, case-insensitive substring. */
    search: z.string().trim().min(1).max(200).optional(),
});
export const adminSetUserStatusRequestSchema = z.object({
    status: z.enum([UserStatus.ACTIVE, UserStatus.SUSPENDED]),
});
export const adminMetricsSchema = z.object({
    totalUsers: z.number().int().nonnegative(),
    suspendedUsers: z.number().int().nonnegative(),
    totalListings: z.number().int().nonnegative(),
    publishedListings: z.number().int().nonnegative(),
    totalOrders: z.number().int().nonnegative(),
    fundedOrders: z.number().int().nonnegative(),
    releasedOrders: z.number().int().nonnegative(),
    disputedOrders: z.number().int().nonnegative(),
    openDisputes: z.number().int().nonnegative(),
    totalPlatformFeeRevenueBaseUnits: bigintBaseUnitsSchema,
    totalReviews: z.number().int().nonnegative(),
    averageRating: z.number().min(1).max(5).nullable(),
    flaggedMessagesLast7d: z.number().int().nonnegative(),
});
export const adminFlaggedMessageSchema = z.object({
    id: uuidSchema,
    actorId: uuidSchema.nullable(),
    actorUsername: z.string().nullable(),
    conversationId: uuidSchema.nullable(),
    kind: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const adminAuditEventSchema = z.object({
    id: uuidSchema,
    actorId: uuidSchema.nullable(),
    actorUsername: z.string().nullable(),
    orderId: uuidSchema.nullable(),
    action: z.string(),
    metadata: z.unknown().nullable(),
    createdAt: z.string().datetime(),
});
export const adminListAuditEventsQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    action: z.string().optional(),
});
export const maintenanceModeResponseSchema = z.object({
    enabled: z.boolean(),
    message: z.string().nullable(),
    updatedAt: z.string().datetime(),
});
export const setMaintenanceModeRequestSchema = z.object({
    enabled: z.boolean(),
    message: z.string().trim().max(500).optional(),
});
// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
export const paginationQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export function paginatedResponseSchema(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
    });
}
// ---------------------------------------------------------------------------
// Contact form — public, unauthenticated, Turnstile-gated (see apps/api/src/routes/contact.ts).
// ---------------------------------------------------------------------------
export const contactRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
    email: z.string().trim().email().max(200),
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(5_000),
});
export const contactResponseSchema = z.object({ sent: z.boolean() });
// ---------------------------------------------------------------------------
// Support tickets — separate from disputes (see apps/api/src/routes/tickets.ts /
// packages/database's Ticket model docblock for why).
// ---------------------------------------------------------------------------
export const createTicketRequestSchema = z
    .object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    contactMethod: z.enum([TicketContactMethod.EMAIL, TicketContactMethod.PHONE]),
    /** Required only when `contactMethod` is PHONE — checked below since it's conditional. */
    contactPhone: z.string().trim().max(40).optional(),
    subject: z.string().trim().min(1).max(200),
    message: z.string().trim().min(1).max(5_000),
})
    .refine((v) => v.contactMethod !== TicketContactMethod.PHONE || Boolean(v.contactPhone), {
    message: "A phone number is required when the contact method is Phone",
    path: ["contactPhone"],
});
export const ticketMessageResponseSchema = z.object({
    id: uuidSchema,
    ticketId: uuidSchema,
    isFromAdmin: z.boolean(),
    /** The user's display name for a user-authored message, or "Support" for a staff message —
     *  never the user's email (same privacy stance as everywhere else this app shows a name). */
    authorName: z.string(),
    body: z.string(),
    createdAt: z.string().datetime(),
});
export const createTicketMessageRequestSchema = z.object({
    body: z.string().trim().min(1).max(5_000),
});
const ticketStatusEnum = z.enum([
    TicketStatus.NEW,
    TicketStatus.PENDING,
    TicketStatus.RESOLVED,
    TicketStatus.CANCELLED,
]);
const ticketPriorityEnum = z.enum([
    TicketPriority.CRITICAL,
    TicketPriority.HIGH,
    TicketPriority.NORMAL,
    TicketPriority.LOW,
]);
export const ticketResponseSchema = z.object({
    id: uuidSchema,
    firstName: z.string(),
    lastName: z.string(),
    contactMethod: z.enum([TicketContactMethod.EMAIL, TicketContactMethod.PHONE]),
    contactPhone: z.string().nullable(),
    /** Only ever the ticket owner's own account email, snapshotted at creation — see the Ticket
     *  model's docblock. Present here because this response is only ever returned to the ticket's
     *  owner or to staff, never to any other end user. */
    email: z.string(),
    subject: z.string(),
    status: ticketStatusEnum,
    priority: ticketPriorityEnum,
    ipAddress: z.string().nullable(),
    locationLabel: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ticketDetailResponseSchema = ticketResponseSchema.extend({
    messages: z.array(ticketMessageResponseSchema),
});
export const adminSetTicketStatusRequestSchema = z.object({ status: ticketStatusEnum });
export const adminSetTicketPriorityRequestSchema = z.object({ priority: ticketPriorityEnum });
export const adminListTicketsQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: ticketStatusEnum.optional(),
});
//# sourceMappingURL=schemas.js.map